import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isUnansweredRequestError } from '@/utils/unansweredRequest';
import { AuthContextType, AuthState, ClinicLoginResult, LoginCredentials } from '@app-types/auth';
import {
  useMainGetUser,
  useMainLogin,
  useMainLogout,
  useMainRefreshToken,
  useUnifiedTenantGetUser,
  useUnifiedTenantLogout,
  useUnifiedTenantRefreshToken,
  type UnifiedTenantPrincipalType,
} from '@/hooks/main/useMainAuth';
import { authorizeNavigation } from '@/hooks/useFormConfirmationBlocker';
import { getRouter } from '@/shared/lib/routerRef';
import { logoutSsoContext } from '@/features/sso/ssoLogout';
import { notifyTerminalSsoExpiry } from '@/features/sso/terminalSsoExpiry';
import {
  isTerminalLogoutErrorHandled,
  isTerminalLogoutUnavailable,
  isUnavailableLogoutCurrent,
  registerTerminalLogoutHandler,
  terminalLogoutDestination,
  type TerminalLogoutIntent,
  type TerminalLogoutOutcome,
} from '@/features/sso/terminalLogoutCoordinator';
import {
  captureActiveSessionAuthGeneration,
  clearActiveSessionSyncReload,
  emitActiveSessionAuthGeneration,
  isActiveSessionAuthGenerationCurrent,
  readActiveSessionSyncReload,
  runForCurrentActiveSessionAuthGeneration,
  settleActiveSessionSyncCooldown,
} from '@/features/sso/activeSessionSync';
import { hideSsoSyncCurtain } from '@/features/sso/ssoSyncCurtain';
import {
  assignTerminalPassiveRecoveryLocation,
  clearTerminalPassiveRecovery,
  selectTerminalPassiveRecoveryDestination,
} from '@/features/sso/terminalPassiveRecovery';
import {
  canRecoverAnonymousP1ClinicMutation,
  createUnifiedAbandonedMutationRecovery,
  runP1SsoAuthMutation,
} from '@/features/sso/ssoAuthMutationLock';
import {
  buildSsoLoginUrl,
  clearPortalSession,
  clearSsoSession,
  hasSsoSession,
  isSsoEnabled,
  isSsoSite,
  markPortalAuthChannel,
  markSsoSession,
  normalizeUnifiedPortalStorage,
  readActivePortalAuthChannel,
  readPortalAuthTransport,
  selectExactPortalBearerToken,
  ssoReturnPath,
  type PortalAuthTransport,
  type SsoEntryType,
  validateSsoLogoutUrl,
} from '@/features/sso/ssoSession';

const deriveSiteFromLocation = (): string => {
  const match = window.location.pathname.match(/^\/([^/]+)(?:\/|$)/);
  return match?.[1] || 'medix';
};
const SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY = 'suppress_force_logout_toast_once';
const HIDE_TENANT_LOGOUT_SESSION_KEY = 'ra2n_hide_tenant_logout';

export interface StoredAuthSnapshot {
  token: string | null;
  user: AuthState['user'];
  clinic: AuthState['clinic'];
  entryType: SsoEntryType;
  principalType: UnifiedTenantPrincipalType;
  transport: PortalAuthTransport | null;
}

/**
 * The session check got no answer at all — the connection is down or the server out of reach —
 * as opposed to an answer that says the session is gone. Common's network rule applies: the
 * global toast 「インターネット接続エラーが発生しました。」, and the screen and session kept as they are.
 */
export const isSessionCheckUnanswered = isUnansweredRequestError;

export const isUsableStoredAuthSnapshot = (snapshot: StoredAuthSnapshot): boolean =>
  Boolean(
    snapshot.user &&
    (snapshot.transport === 'cookie' ||
      (snapshot.transport === 'bearer' && snapshot.token))
  );

const safeJsonRecord = (value: string | null): Record<string, unknown> | null => {
  if (!value) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const principalTypeOf = (user: Record<string, unknown> | null): UnifiedTenantPrincipalType => {
  const normalized = String(user?.principal_type ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (normalized === 'admin' || normalized.endsWith('admin')) return 'admin';
  if (['massager', 'practitioner', 'therapist'].includes(normalized)) return 'massager';
  return 'clinic_user';
};

export const readStoredAuthSnapshot = (
  mode: AuthProviderMode,
  site: string,
): StoredAuthSnapshot => {
  if (mode === 'unified' && isSsoSite(site)) {
    normalizeUnifiedPortalStorage(site, localStorage, sessionStorage);
  }
  const clinicToken = localStorage.getItem('auth_token');
  const clinicUser = safeJsonRecord(localStorage.getItem('auth_user'));
  const clinic = safeJsonRecord(localStorage.getItem('auth_clinic'));

  if (mode !== 'unified') {
    const clinicContext = isSsoSite(site)
      ? { site, entryType: 'clinic' } as const
      : undefined;
    const transport = clinicContext
      ? readPortalAuthTransport(clinicContext, localStorage) ??
        (hasSsoSession(clinicContext) ? 'cookie' : clinicToken ? 'bearer' : null)
      : clinicToken
        ? 'bearer'
        : null;
    return {
      token: transport === 'cookie' ? null : clinicToken,
      user: clinicUser as AuthState['user'],
      clinic: clinic as AuthState['clinic'],
      entryType: 'clinic',
      principalType: principalTypeOf(clinicUser),
      transport,
    };
  }

  // A unified tenant has exactly one browser auth channel. Principal type is
  // payload metadata used by RBAC, never a selector for another token/cookie.
  const channel = isSsoSite(site)
    ? readActivePortalAuthChannel(site, localStorage)
    : null;
  const selectedEntry = 'unified' as const;
  const selectedContext = { site, entryType: selectedEntry } as const;
  const unifiedChannel = channel?.entryType === selectedEntry ? channel : null;
  const selectedToken = unifiedChannel === null
    ? clinicToken
    : selectExactPortalBearerToken(selectedContext, localStorage);
  const legacyCookieSession = isSsoSite(site) && hasSsoSession(selectedContext);
  const transport =
    unifiedChannel?.transport ??
    (legacyCookieSession ? 'cookie' : selectedToken ? 'bearer' : null);

  return {
    token: transport === 'bearer' ? selectedToken : null,
    user: clinicUser as AuthState['user'],
    clinic: clinic as AuthState['clinic'],
    entryType: selectedEntry,
    principalType: principalTypeOf(clinicUser),
    transport,
  };
};

const isP1SsoAuthMutationEnabled = (): boolean =>
  [
    import.meta.env.VITE_SSO_ENABLED,
    import.meta.env.VITE_SSO_PASSIVE_ENABLED,
    import.meta.env.VITE_SSO_ACTIVE_SESSION_SYNC_ENABLED,
  ].some(isSsoEnabled);

function readHideTenantLogoutFromSession(): boolean {
  return sessionStorage.getItem(HIDE_TENANT_LOGOUT_SESSION_KEY) === '1';
}

function setHideTenantLogoutSession(value: boolean): void {
  if (value) {
    sessionStorage.setItem(HIDE_TENANT_LOGOUT_SESSION_KEY, '1');
  } else {
    sessionStorage.removeItem(HIDE_TENANT_LOGOUT_SESSION_KEY);
  }
}

/**
 * Authentication Context
 *
 * Provides authentication state and methods throughout the app.
 */

const initialState: AuthState = {
  user: null,
  clinic: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  hideTenantLogoutButton: false,
  authEntryType: 'clinic',
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export type AuthProviderMode = 'clinic' | 'passive' | 'unified';

interface AuthProviderProps {
  children: React.ReactNode;
  mode?: AuthProviderMode;
  recoverStaleSessionHydrationViaPassiveSso?: boolean;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({
  children,
  mode = 'clinic',
  recoverStaleSessionHydrationViaPassiveSso = false,
}) => {
  const [state, setState] = useState<AuthState>(initialState);

  const loginMutation = useMainLogin();
  const logoutMutation = useMainLogout();
  const refreshTokenMutation = useMainRefreshToken();
  const unifiedLogoutMutation = useUnifiedTenantLogout();
  const unifiedRefreshMutation = useUnifiedTenantRefreshToken();
  const queryClient = useQueryClient();

  const derivedSite = deriveSiteFromLocation();
  const isPassiveMode = mode === 'passive';
  const isUnifiedMode = mode === 'unified';
  const [storedAuth, setStoredAuth] = useState<StoredAuthSnapshot>(() =>
    readStoredAuthSnapshot(mode, derivedSite)
  );
  const authContext = isSsoSite(derivedSite)
    ? { site: derivedSite, entryType: storedAuth.entryType } as const
    : undefined;
  const [initialTerminalRecoveryEligible] = useState(() => {
    if (
      !recoverStaleSessionHydrationViaPassiveSso ||
      !isUnifiedMode ||
      storedAuth.transport !== 'cookie' ||
      !isSsoSite(derivedSite)
    ) {
      return false;
    }
    return window.location.pathname === ssoReturnPath({
      site: derivedSite,
      entryType: storedAuth.entryType,
    });
  });
  const hasCookieSession = storedAuth.transport === 'cookie' || Boolean(
    authContext && hasSsoSession(authContext)
  );
  const authGeneration = authContext
    ? captureActiveSessionAuthGeneration(authContext, localStorage).generation
    : 0;
  const mainGetUserQuery = useMainGetUser(
    isPassiveMode || isUnifiedMode ? undefined : derivedSite
  );
  const unifiedGetUserQuery = useUnifiedTenantGetUser({
    site: derivedSite,
    principalType: storedAuth.principalType,
    principalId: storedAuth.user?.id,
    authGeneration,
    bearerToken: storedAuth.transport === 'bearer' ? storedAuth.token : null,
    enabled:
      isUnifiedMode &&
      !isPassiveMode &&
      !!storedAuth.user &&
      (storedAuth.transport === 'cookie' ||
        (storedAuth.transport === 'bearer' && !!storedAuth.token)),
    terminalRecoveryEligible: initialTerminalRecoveryEligible,
  });
  const getUserQuery = isUnifiedMode ? unifiedGetUserQuery : mainGetUserQuery;

  // A session check that got no answer keeps the stored session (see below); ask again once the
  // browser is back online, since the app's QueryClient does not refetch on reconnect.
  const sessionCheckUnanswered = getUserQuery.isError && isSessionCheckUnanswered(getUserQuery.error);
  const refetchSession = getUserQuery.refetch;
  useEffect(() => {
    if (!sessionCheckUnanswered) return undefined;
    const retry = () => void refetchSession();
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, [sessionCheckUnanswered, refetchSession]);

  // Initialize auth state from storage / server
  useEffect(() => {
    if (isPassiveMode) {
      setState({
        user: storedAuth.user,
        clinic: storedAuth.clinic,
        token: storedAuth.token,
        isAuthenticated: !!storedAuth.token && !!storedAuth.user,
        isLoading: false,
        error: null,
        hideTenantLogoutButton: readHideTenantLogoutFromSession(),
        authEntryType: storedAuth.entryType,
      });
      return;
    }

    if ((!storedAuth.token && !hasCookieSession) || !storedAuth.user) {
      setHideTenantLogoutSession(false);
      setState({
        ...initialState,
        isLoading: false,
        hideTenantLogoutButton: false,
        authEntryType: storedAuth.entryType,
      });
      return;
    }

    if (getUserQuery.isSuccess) {
      if (authContext && storedAuth.transport) {
        markPortalAuthChannel(authContext, storedAuth.transport, localStorage);
        if (recoverStaleSessionHydrationViaPassiveSso) {
          if (storedAuth.transport === 'cookie') {
            try {
              markSsoSession(authContext, sessionStorage);
            } catch {
              // Browser storage is only a recovery hint; authenticated server state wins.
            }
          }
          if (clearTerminalPassiveRecovery(authContext, sessionStorage)) {
            try {
              sessionStorage.removeItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
            } catch {
              // Never turn successful hydration into an auth failure over a toast hint.
            }
          }
        }
      }
      setState({
        user: getUserQuery.data.user,
        clinic: getUserQuery.data.clinic,
        token: storedAuth.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        hideTenantLogoutButton: readHideTenantLogoutFromSession(),
        authEntryType: storedAuth.entryType,
      });
      return;
    }

    if (getUserQuery.isError) {
      if (isTerminalLogoutUnavailable(getUserQuery.error)) {
        if (isUnavailableLogoutCurrent(getUserQuery.error)) {
          // Do not clear cookies/storage outside the mutex, or expose business UI.
          // A failed coordinator is not a completed logout: settle this tab's
          // bootstrap and leave a stable login form for an explicit retry.
          setStoredAuth({ token: null, user: null, clinic: null,
            entryType: storedAuth.entryType, principalType: 'clinic_user', transport: null });
          setState({ ...initialState, isLoading: false, authEntryType: storedAuth.entryType });
          const reload = readActiveSessionSyncReload();
          if (reload?.context.site === derivedSite
            && reload.context.entryType === storedAuth.entryType
            && reload.phase !== 'redirecting'
            && clearActiveSessionSyncReload(reload)) {
            hideSsoSyncCurtain(reload.curtainRevision);
          }
          authorizeNavigation();
          getRouter().navigate(`/${derivedSite}/login?sso_passive=checked`, { replace: true });
        }
        return;
      }
      if (isTerminalLogoutErrorHandled(getUserQuery.error)) {
        if (!isUnifiedMode) return;
        // A session ended from another tab is superseded here: no logout runs in
        // this tab, so it follows the shared storage itself. It drops what the
        // ended session cached, then hydrates the session stored there since, or
        // settles on the checked login when there is none to converge on.
        const converged = readStoredAuthSnapshot(mode, derivedSite);
        if (!isUsableStoredAuthSnapshot(converged)) {
          setStoredAuth({ token: null, user: null, clinic: null,
            entryType: storedAuth.entryType, principalType: 'clinic_user', transport: null });
          setState({ ...initialState, isLoading: false, authEntryType: storedAuth.entryType });
          queryClient.clear();
          authorizeNavigation();
          getRouter().navigate(`/${derivedSite}/login?sso_passive=checked`, { replace: true });
        } else if (converged.token !== storedAuth.token
          || converged.transport !== storedAuth.transport) {
          setStoredAuth(converged);
          setState(previous => ({ ...previous, isLoading: true }));
          // The session query key holds no credential: removing the failed query
          // is what makes it run again, now with the converged one.
          queryClient.clear();
        }
        return;
      }
      const status = (getUserQuery.error as { response?: { status?: number } })?.response?.status;
      // Neither a 403 nor a check that got no answer says the session ended: keep what is stored
      // and stay on the screen. A dropped connection used to land here as "invalid session",
      // wiping the stored login and sending the operator to the login page; the session query
      // runs again once the connection is back (React Query's reconnect refetch).
      if (status === 403 || isSessionCheckUnanswered(getUserQuery.error)) {
        setState(previous => ({
          ...previous,
          user: storedAuth.user,
          clinic: storedAuth.clinic,
          token: storedAuth.token,
          isAuthenticated: isUsableStoredAuthSnapshot(storedAuth),
          isLoading: false,
          authEntryType: storedAuth.entryType,
        }));
        return;
      }
      if (isSsoSite(derivedSite)) {
        clearPortalSession(
          { site: derivedSite, entryType: storedAuth.entryType },
          localStorage,
          sessionStorage,
        );
      } else if (storedAuth.entryType === 'admin') {
        localStorage.removeItem('site_admin_token');
        localStorage.removeItem('site_admin');
        localStorage.removeItem('site_admin_site');
      } else {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        localStorage.removeItem('auth_clinic');
      }
      setHideTenantLogoutSession(false);
      setState({
        ...initialState,
        isLoading: false,
        hideTenantLogoutButton: false,
      });
      return;
    }

    // While verifying
    setState(prev => ({
      ...prev,
      isLoading: true,
    }));
  }, [
    getUserQuery.isSuccess,
    getUserQuery.isError,
    getUserQuery.data,
    isPassiveMode,
    isUnifiedMode,
    storedAuth.token,
    storedAuth.user,
    storedAuth.clinic,
    storedAuth.entryType,
    storedAuth.principalType,
    storedAuth.transport,
    hasCookieSession,
    authGeneration,
    derivedSite,
  ]);

  const login = async (credentials: LoginCredentials): Promise<ClinicLoginResult> => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    const loginSite = credentials.site_type || derivedSite;
    const loginEntryType = isUnifiedMode ? 'unified' : 'clinic';
    const context = isSsoSite(loginSite)
      ? { site: loginSite, entryType: loginEntryType } as const
      : undefined;
    const ssoEnabled = context !== undefined && isP1SsoAuthMutationEnabled();
    const performLogin = async (): Promise<ClinicLoginResult> => {
      try {
        const response = await loginMutation.mutateAsync({
          ...credentials,
          authEntryType: loginEntryType,
        });

        if (response.success) {
          if (context) {
            clearSsoSession(context);
            if (recoverStaleSessionHydrationViaPassiveSso) {
              clearTerminalPassiveRecovery(context, sessionStorage);
            }
          }
          const hideLogout = !!response.data.from_admin;
          setHideTenantLogoutSession(hideLogout);
          sessionStorage.removeItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
          setStoredAuth({
            token: response.data.token,
            user: response.data.user,
            clinic: response.data.clinic,
            entryType: loginEntryType,
            principalType: principalTypeOf(
              response.data.user as unknown as Record<string, unknown>
            ),
            transport: 'bearer',
          });
          setState({
            user: response.data.user,
            clinic: response.data.clinic,
            token: response.data.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            hideTenantLogoutButton: hideLogout,
            authEntryType: loginEntryType,
          });
          if (ssoEnabled && context) {
            emitActiveSessionAuthGeneration(context, localStorage, window);
            // This sign-in just settled which session the browser holds, so the page it lands
            // on has nothing left to pick up from another Portal. Without the cooldown that
            // page opened one more broker round trip and the screen visibly flashed twice
            // after logging in, ending on a URL that briefly read /sso/... on the way past.
            settleActiveSessionSyncCooldown(context, sessionStorage, localStorage);
          }
          return {
            success: true,
            ssoEstablishUrl: response.data.sso_establish_url,
          };
        }

        const failMessage = response.message || 'ログインに失敗しました';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: failMessage,
        }));
        return { success: false, message: failMessage };
      } catch (error: unknown) {
        const axiosError = error as {
          response?: {
            data?: { message?: string; errors?: { password?: string[] } };
          };
        };
        const data = axiosError?.response?.data;
        const message =
          data?.errors?.password?.[0] ||
          data?.message ||
          (error instanceof Error ? error.message : null) ||
          'ログインに失敗しました';
        setState(prev => ({
          ...prev,
          isLoading: false,
          error: message,
        }));
        return { success: false, message };
      }
    };

    if (!context) {
      return performLogin();
    }

    try {
      const loginGeneration = captureActiveSessionAuthGeneration(context, localStorage);
      const result = await runP1SsoAuthMutation({
        context,
        ssoEnabled,
        storage: localStorage,
        operation: performLogin,
        // A page-load active-sync probe may commit a no-op while this click is
        // queued for the same Web Lock. The login is still safe only when the
        // exact click-time anonymous/auth generation remains current.
        canRunAfterCommittedMutation: () =>
          isActiveSessionAuthGenerationCurrent(loginGeneration, localStorage),
        canRecoverAbandonedMutation: createUnifiedAbandonedMutationRecovery({
          context,
          storage: localStorage,
          sessionStorage,
          isCurrent: () => isActiveSessionAuthGenerationCurrent(loginGeneration, localStorage),
        }) ?? (() => canRecoverAnonymousP1ClinicMutation({
            context,
            authGeneration: loginGeneration,
            localStorage,
            sessionStorage,
          })),
      });
      if (result.kind === 'completed') {
        return result.value;
      }
      if (
        result.reason === 'stale_intent' &&
        !isActiveSessionAuthGenerationCurrent(loginGeneration, localStorage)
      ) {
        const converged = readStoredAuthSnapshot(mode, loginSite);
        if (isUsableStoredAuthSnapshot(converged)) {
          if (
            recoverStaleSessionHydrationViaPassiveSso &&
            clearTerminalPassiveRecovery(context, sessionStorage)
          ) {
            try {
              sessionStorage.removeItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY);
            } catch {
              // The converged session is authoritative even if browser hints are blocked.
            }
          }
          setStoredAuth(converged);
          setState({
            user: converged.user,
            clinic: converged.clinic,
            token: converged.token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            hideTenantLogoutButton: false,
            authEntryType: converged.entryType,
          });
          settleActiveSessionSyncCooldown(context, sessionStorage, localStorage);
          return { success: true };
        }
      }
    } catch {
      // The response could not be committed safely. Keep the current session unchanged.
    }

    const message = 'ログインを安全に完了できませんでした。もう一度お試しください。';
    setState(prev => ({ ...prev, isLoading: false, error: message }));
    return { success: false, message };
  };

  const logoutCurrentSession = async (terminalIntent?: TerminalLogoutIntent): Promise<TerminalLogoutOutcome> => {
    let finalized = false;
    let terminalRecoveryDestination: string | undefined;
    let terminalExpirySettled = false;
    const resolvedSite = state.clinic?.parent_site_type || state.clinic?.site_type || derivedSite;
    const logoutSite = resolvedSite.toLowerCase().includes('upsystem') ? 'medix' : resolvedSite;
    const entryType = isUnifiedMode ? storedAuth.entryType : 'clinic';
    const loginPath = entryType === 'admin'
      ? `/${logoutSite}/admin/login`
      : `/${logoutSite}/login`;
    const portalContext = isSsoSite(logoutSite)
      ? { site: logoutSite, entryType } as const
      : undefined;
    const activeChannel = portalContext
      ? readActivePortalAuthChannel(portalContext.site, localStorage)
      : null;
    const ssoContext = portalContext &&
      (hasSsoSession(portalContext) ||
        activeChannel?.entryType === entryType && activeChannel.transport === 'cookie')
        ? portalContext
        : undefined;
    const generationContext = isSsoSite(logoutSite)
      ? { site: logoutSite, entryType } as const
      : undefined;
    const logoutGeneration = generationContext
      ? captureActiveSessionAuthGeneration(generationContext, localStorage)
      : undefined;
    const ssoEnabled = generationContext !== undefined && isP1SsoAuthMutationEnabled();
    let redirectUrl = terminalIntent ? `${loginPath}?sso_passive=checked` : loginPath;
    const isLogoutIntentCurrent = () => {
      if (terminalIntent && !terminalIntent.isCurrent()) return false;
      // A terminal intent already pins the exact stored credential the server
      // rejected (plus generation, principal and clinic). The provider's own
      // copy may lag storage; comparing against it would read a dead stored
      // token as "superseded" and leave the tab on a session that can no
      // longer call the API.
      if (!terminalIntent && storedAuth.transport === 'bearer') {
        const token = generationContext
          ? selectExactPortalBearerToken(generationContext, localStorage)
          : localStorage.getItem(entryType === 'admin' ? 'site_admin_token' : 'auth_token');
        if (token !== storedAuth.token) return false;
      }
      return !logoutGeneration || isActiveSessionAuthGenerationCurrent(logoutGeneration, localStorage);
    };

    const performLogout = async (): Promise<void> => {
      if (!isLogoutIntentCurrent()) return;
      try {
        if (terminalIntent && ssoContext) {
          terminalExpirySettled = await notifyTerminalSsoExpiry({
            context: terminalIntent.context,
            generation: terminalIntent.generation,
            authorization: terminalIntent.authorization,
            localStorage,
            force: true,
          }) === 'settled';
          if (!isLogoutIntentCurrent()) return;
        }
        if (ssoContext) {
          redirectUrl = await logoutSsoContext(ssoContext);
        } else {
          const response = isUnifiedMode
            ? await unifiedLogoutMutation.mutateAsync({
                site: derivedSite,
                entryType,
                bearerToken: storedAuth.token,
              })
            : await logoutMutation.mutateAsync({ site: derivedSite });
          redirectUrl = validateSsoLogoutUrl(
            response?.data?.logout_url,
            isSsoSite(logoutSite)
              ? { site: logoutSite, entryType }
              : undefined,
          ) ?? redirectUrl;
        }
      } catch (error) {
        if (!terminalIntent) console.error('Logout failed:', error);
      } finally {
        const finalizeLogout = (): void => {
          if (!isLogoutIntentCurrent()) return;
          finalized = true;
          if (terminalIntent) {
            redirectUrl = terminalLogoutDestination(redirectUrl, loginPath, window.location.origin);
          }
          if (terminalIntent && generationContext) {
            terminalRecoveryDestination = selectTerminalPassiveRecoveryDestination({
              enabled: recoverStaleSessionHydrationViaPassiveSso,
              cause: terminalIntent.cause,
              recoveryEligible:
                terminalIntent.recoveryEligible && terminalExpirySettled,
              context: generationContext,
              checkedLoginDestination: redirectUrl,
              expectedCheckedLoginDestination: `${loginPath}?sso_passive=checked`,
              passiveSsoEnabled: import.meta.env.VITE_SSO_PASSIVE_ENABLED,
              storage: sessionStorage,
              buildDestination: () => {
                const apiOrigin = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
                return buildSsoLoginUrl(apiOrigin, {
                  ...generationContext,
                  prompt: 'none',
                });
              },
            });
            redirectUrl = terminalRecoveryDestination ?? redirectUrl;
          }
          sessionStorage.setItem(SUPPRESS_FORCE_LOGOUT_TOAST_ONCE_KEY, '1');
          setHideTenantLogoutSession(false);
          if (generationContext) {
            clearPortalSession(generationContext, localStorage, sessionStorage);
          } else if (entryType === 'admin') {
            localStorage.removeItem('site_admin_token');
            localStorage.removeItem('site_admin');
            localStorage.removeItem('site_admin_site');
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            localStorage.removeItem('auth_clinic');
          }
          setStoredAuth({
            token: null,
            user: null,
            clinic: null,
            entryType: storedAuth.entryType,
            principalType: 'clinic_user',
            transport: null,
          });
          setState({ ...initialState, hideTenantLogoutButton: false });
          queryClient.clear();
          authorizeNavigation();
          if (ssoEnabled && generationContext) {
            emitActiveSessionAuthGeneration(generationContext, localStorage, window);
          }
          if (terminalRecoveryDestination) {
            assignTerminalPassiveRecoveryLocation(terminalRecoveryDestination);
          } else if (redirectUrl !== loginPath
            && redirectUrl !== `${loginPath}?sso_passive=checked`) {
            window.location.assign(redirectUrl);
          } else {
            getRouter().navigate(redirectUrl, { replace: true });
          }
        };

        // `runForCurrentActiveSessionAuthGeneration` runs `finalizeLogout`
        // only while this tab still owns the generation it captured; when it
        // does not, there is nothing left to do. This is a `finally` block, so
        // it must not `return` — a return here would discard whatever the
        // `try` was completing.
        if (!logoutGeneration) {
          finalizeLogout();
        } else {
          runForCurrentActiveSessionAuthGeneration(
            logoutGeneration,
            localStorage,
            finalizeLogout,
          );
        }
      }
    };

    if (!generationContext) {
      await performLogout();
      return finalized ? 'handled' : 'unavailable';
    }

    try {
      await runP1SsoAuthMutation({
        context: generationContext,
        ssoEnabled,
        storage: localStorage,
        operation: performLogout,
        canRunAfterCommittedMutation: isLogoutIntentCurrent,
        canRecoverAbandonedMutation: createUnifiedAbandonedMutationRecovery({
          context: generationContext,
          storage: localStorage,
          sessionStorage,
          isCurrent: isLogoutIntentCurrent,
        }),
      });

    } catch {
      // Fail closed: do not start a second, uncoordinated logout mutation.
    }
    return finalized ? 'handled' : isLogoutIntentCurrent() ? 'unavailable' : 'superseded';
  };

  const logout = async (): Promise<void> => { await logoutCurrentSession(); };
  const terminalLogoutRef = useRef(logoutCurrentSession);
  terminalLogoutRef.current = logoutCurrentSession;
  useLayoutEffect(() => {
    if (isPassiveMode || !isSsoSite(derivedSite)) return;
    return registerTerminalLogoutHandler(
      { site: derivedSite, entryType: storedAuth.entryType },
      intent => terminalLogoutRef.current(intent),
    );
  }, [derivedSite, isPassiveMode, storedAuth.entryType]);

  const refreshToken = async (): Promise<void> => {
    const entryType = isUnifiedMode ? storedAuth.entryType : 'clinic';
    const context = isSsoSite(derivedSite)
      ? { site: derivedSite, entryType } as const
      : undefined;
    const ssoEnabled = context !== undefined && isP1SsoAuthMutationEnabled();
    const performRefresh = async (): Promise<boolean> => {
      try {
        const response = isUnifiedMode
          ? await unifiedRefreshMutation.mutateAsync({
              site: derivedSite,
              entryType,
              bearerToken: storedAuth.token,
            })
          : await refreshTokenMutation.mutateAsync({ site: derivedSite });
        if (response.success && (response.data?.token || hasCookieSession)) {
          if (response.data?.token) {
            const token = response.data.token;
            const credentialKey = entryType === 'admin'
              ? 'site_admin_token'
              : 'auth_token';
            localStorage.setItem(credentialKey, token);
            if (context) {
              markPortalAuthChannel(context, 'bearer', localStorage);
            }
            setStoredAuth(previous => ({ ...previous, token, transport: 'bearer' }));
            setState(prev => ({
              ...prev,
              token,
            }));
          }
          if (ssoEnabled && context) {
            emitActiveSessionAuthGeneration(context, localStorage, window);
          }
          return true;
        }
      } catch {
        // Run logout only after the mutation lock has been released.
      }
      return false;
    };

    if (!context) {
      if (!await performRefresh()) {
        await logout();
      }
      return;
    }

    let shouldLogout = false;
    try {
      const result = await runP1SsoAuthMutation({
        context,
        ssoEnabled,
        storage: localStorage,
        operation: performRefresh,
        canRecoverAbandonedMutation: createUnifiedAbandonedMutationRecovery({
          context,
          storage: localStorage,
          sessionStorage,
        }),
      });
      shouldLogout = result.kind === 'completed' && !result.value;
    } catch {
      // Fail closed when the response could not be committed safely.
      return;
    }

    if (shouldLogout) {
      await logout();
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to use auth context
 * Returns a default safe auth context if provider is not available (e.g., during HMR)
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);

  // Return context from localStorage if provider is not available (e.g., during HMR reloads)
  // This prevents ProtectedRoute from being stuck on "施設情報を読み込み中..." after HMR
  if (context === undefined) {
    const storedClinic = localStorage.getItem('auth_clinic');
    const storedUser = localStorage.getItem('auth_user');
    const storedToken = localStorage.getItem('auth_token');
    const clinic = storedClinic ? JSON.parse(storedClinic) : null;
    const user = storedUser ? JSON.parse(storedUser) : null;

    return {
      user,
      clinic: clinic ?? {
        id: 0,
        clinic_number: '',
        name: 'Default Clinic',
        site_type: 'medix',
        service_type: 'a2',
        is_enable_checkin: false,
      },
      token: storedToken,
      isAuthenticated: !!storedToken && !!user,
      isLoading: false,
      error: null,
      hideTenantLogoutButton: false,
      authEntryType: 'clinic',
      login: async () => ({ success: true }),
      logout: async () => {},
      refreshToken: async () => {},
    };
  }

  return context;
};

export default AuthContext;
